import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TailoringRunStatus, getTailoringRunStatus, subscribeTailoringRunStatus } from '@/lib/tailoringRunStatus';
import { getQuickStartPhase, getQuickStartProgress, subscribeQuickStartProgress, quickStartPercentDone, QUICK_START_STEPS, type QuickStartProgress } from '@/lib/tailoringQuickStart';

const TAILORING_PATH = '/resume-tailoring-claude-direct';

/** Floating pill shown anywhere in the app while a tailoring run is active and
 *  the user has navigated away from the tailoring page. Clicking returns to
 *  the live run. The run itself never stops: the tailoring page stays mounted
 *  (hidden) across route changes. */
export default function TailoringProgressPill() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<TailoringRunStatus>(getTailoringRunStatus());

  useEffect(() => subscribeTailoringRunStatus(setStatus), []);
  const [qp, setQp] = useState<QuickStartProgress>(getQuickStartProgress());
  useEffect(() => subscribeQuickStartProgress(setQp), []);
  // Honest progress: share of steps finished (not a timer). Falls back to the
  // engine's own number only when no step list is reporting.
  const stepList = QUICK_START_STEPS.filter((s) => !s.deepOnly || qp.deep);
  const hasSteps = stepList.some((s) => qp.steps[s.id]);
  const pct = hasSteps ? quickStartPercentDone(qp) : status.progress;
  const running = stepList.filter((s) => qp.steps[s.id] === 'active').map((s) => s.active);

  // The dashboard home shows the run under its tailoring box, so no pill there.
  const onDashboardHome = (location.pathname === '/' || location.pathname === '/dashboard') && !new URLSearchParams(location.search).get('view');
  if (!status.active || location.pathname === TAILORING_PATH || onDashboardHome) return null;

  const mins = Math.floor(status.elapsedSeconds / 60);
  const secs = status.elapsedSeconds % 60;

  return (
    <button
      onClick={() => navigate(getQuickStartPhase().phase === 'running' ? '/' : TAILORING_PATH)}
      className="fixed bottom-6 right-6 z-[100] flex items-center gap-3 rounded-full bg-slate-900 text-white pl-3 pr-5 py-3 shadow-2xl border border-slate-700 hover:bg-slate-800 transition-colors"
      title="A resume is being tailored. Click to return to the live run."
    >
      <span className="relative flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
      </span>
      <span className="text-sm font-semibold">
        Tailoring… {pct.toFixed(0)}% {hasSteps ? `(${stepList.filter((s) => qp.steps[s.id] === 'done').length} of ${stepList.length} steps)` : ''}
        {running.length > 0 && <span className="block text-[11px] font-normal text-slate-300 max-w-[260px] truncate">{running.join(' + ')}</span>}
        <span className="ml-2 font-normal text-slate-300">{mins}:{String(secs).padStart(2, '0')}</span>
      </span>
    </button>
  );
}
